// Main-thread half of the Worker decode. Ported from event-connect's scanner
// (decoder/worker-decoder.ts). Uses TS `private` instead of JS `#` private fields — Gatsby's SSR
// webpack/babel config doesn't have @babel/plugin-transform-private-methods enabled, and `private`
// compiles away with no runtime private-field semantics needed here.
//
// Implements the frozen `Decoder` interface (decoder/types.ts) unchanged — `detect()` was
// already Promise-returning, which is why this needed no re-gate. The one wrinkle is that a
// canvas cannot cross a Worker boundary, so this reads the ROI's ImageData here (~1-3ms) and
// TRANSFERS the buffer (zero-copy) rather than structured-cloning ~2MB 15 times a second.

import { boundingBoxOf, normalizeCorners } from '../geometry'
import { THOROUGH } from './decode-options'
import type { Decoder, DecoderCapabilities, Detection } from './types'
import type { DecodeOptions, DecodeRequest, DecodeResponse, WorkerResult } from './worker-protocol'

/** Workers and module workers are near-universal, but this must degrade rather than throw. */
export function workersAvailable(): boolean {
  return typeof window !== 'undefined' && typeof Worker !== 'undefined'
}

type Pending = { resolve: (value: DecodeResponse) => void; reject: (error: Error) => void }

export class WorkerDecoder implements Decoder {
  readonly name = 'zxing-wasm/worker'
  private worker: Worker | null = null
  private pending = new Map<number, Pending>()
  private nextId = 1
  private destroyed = false
  // Instance state, not on the frozen Decoder interface — the one-way downgrade
  // (decode-options.ts) calls setOptions() as the device's own telemetry dictates.
  private options: DecodeOptions = THOROUGH

  /** Addition to the implementation, not to `Decoder` — see decode-options.ts. */
  setOptions(options: DecodeOptions): void {
    this.options = options
  }

  private ensureWorker(): Worker {
    if (this.worker) return this.worker
    const worker = new Worker(new URL('./decode.worker.ts', import.meta.url), { type: 'module' })
    worker.onmessage = (event: MessageEvent<DecodeResponse>) => {
      const entry = this.pending.get(event.data.id)
      if (!entry) return
      this.pending.delete(event.data.id)
      entry.resolve(event.data)
    }
    // A worker-level failure must reject everything in flight, not hang the scan loop forever.
    worker.onerror = () => this.failAll('decode worker failed')
    this.worker = worker
    return worker
  }

  private failAll(message: string): void {
    for (const [, entry] of this.pending) entry.reject(new Error(message))
    this.pending.clear()
  }

  private send(request: DecodeRequest, transfer?: Transferable[]): Promise<DecodeResponse> {
    const worker = this.ensureWorker()
    return new Promise<DecodeResponse>((resolve, reject) => {
      this.pending.set(request.id, { resolve, reject })
      worker.postMessage(request, transfer ?? [])
    })
  }

  async init(formats: readonly string[]): Promise<DecoderCapabilities> {
    // Re-initialising after destroy() is legitimate — the decoder instance outlives the effect
    // that owns it, so a StrictMode double-invoke or remount re-inits the same object.
    this.destroyed = false
    const response = await this.send({ id: this.nextId++, type: 'init', formats })
    if (this.destroyed) return { supportedFormats: [], unsupportedFormats: [...formats] }
    if (response.type === 'error') throw new Error(response.message)
    if (response.type !== 'init') throw new Error('unexpected worker response to init')
    return {
      supportedFormats: response.supportedFormats,
      unsupportedFormats: response.unsupportedFormats,
    }
  }

  async detect(source: HTMLCanvasElement): Promise<Detection[]> {
    if (this.destroyed || !this.worker) throw new Error('WorkerDecoder.detect() called before init()')
    const context = source.getContext('2d', { willReadFrequently: true })
    if (!context) return []

    const imageData = context.getImageData(0, 0, source.width, source.height)
    const buffer = imageData.data.buffer as ArrayBuffer
    const response = await this.send(
      {
        id: this.nextId++,
        type: 'detect',
        buffer,
        width: imageData.width,
        height: imageData.height,
        options: this.options,
      },
      [buffer],
    )

    if (response.type === 'error') throw new Error(response.message)
    if (response.type !== 'detect') return []
    return response.results.map(toDetection)
  }

  destroy(): void {
    this.destroyed = true
    this.failAll('decoder destroyed')
    this.worker?.terminate()
    this.worker = null
  }
}

function toDetection(result: WorkerResult): Detection {
  const { topLeft, topRight, bottomLeft, bottomRight } = result.position
  const cornerPoints = normalizeCorners([topLeft, topRight, bottomRight, bottomLeft])
  return {
    rawValue: result.text,
    format: result.format,
    cornerPoints,
    boundingBox: boundingBoxOf(cornerPoints),
  }
}
